/** Wiederverwendbare Zustandsdarstellungen: Laden, Fehler, leere Menge. */
import { Body1, Button, Spinner, Subtitle2, makeStyles, tokens } from '@fluentui/react-components';
import { ErrorCircleRegular, DocumentSearchRegular, LockClosedRegular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.js';
import type { ApiError } from '../lib/apiClient.js';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXXL,
    textAlign: 'center',
    minHeight: '240px',
  },
  icon: { fontSize: '40px', color: tokens.colorNeutralForeground3 },
  errorIcon: { fontSize: '40px', color: tokens.colorPaletteRedForeground1 },
  hint: { color: tokens.colorNeutralForeground3, maxWidth: '480px' },
  reference: {
    color: tokens.colorNeutralForeground4,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },
});

export function LoadingView({ label }: { label?: string }) {
  const styles = useStyles();
  const { t } = useI18n();
  return (
    <div className={styles.container} role="status" aria-live="polite">
      <Spinner size="large" label={label ?? t('common.loading')} />
    </div>
  );
}

export function ErrorView({ error, onRetry }: { error: ApiError; onRetry?: () => void }) {
  const styles = useStyles();
  const { t } = useI18n();

  const message =
    error.status === 0
      ? t('error.network')
      : error.status === 401
        ? t('error.unauthorized')
        : error.status === 403
          ? t('error.forbidden')
          : error.message || t('error.unexpected');

  return (
    <div className={styles.container} role="alert">
      <ErrorCircleRegular className={styles.errorIcon} aria-hidden />
      <Subtitle2>{t('common.error')}</Subtitle2>
      <Body1 className={styles.hint}>{message}</Body1>
      {error.correlationId ? (
        <Body1 className={styles.reference}>
          {/* Nur eine vom Server gemeldete Id steht auch in dessen Logs. */}
          {error.correlationIdFromServer ? t('error.correlationId') : t('error.correlationIdLocal')}:{' '}
          {error.correlationId}
        </Body1>
      ) : null}
      {onRetry ? <Button appearance="primary" onClick={onRetry}>{t('common.retry')}</Button> : null}
    </div>
  );
}

export function EmptyView({ title, hint, action }: { title?: string; hint?: string; action?: ReactNode }) {
  const styles = useStyles();
  const { t } = useI18n();
  return (
    <div className={styles.container}>
      <DocumentSearchRegular className={styles.icon} aria-hidden />
      <Subtitle2>{title ?? t('common.noData')}</Subtitle2>
      <Body1 className={styles.hint}>{hint ?? t('common.noDataHint')}</Body1>
      {action}
    </div>
  );
}

export function AccessDeniedView() {
  const styles = useStyles();
  const { t } = useI18n();
  return (
    <div className={styles.container} role="alert">
      <LockClosedRegular className={styles.icon} aria-hidden />
      <Subtitle2>{t('common.accessDenied')}</Subtitle2>
      <Body1 className={styles.hint}>{t('common.accessDeniedHint')}</Body1>
    </div>
  );
}
